import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { api } from "../../lib/api"
import { firstName } from "../../lib/format"
import { useUser } from "../../contexts/UserContext"
import { useMe } from "../../hooks/useMe"
import { AppShell } from "../ui/AppShell"
import { ConfirmEmailBanner } from "../Dashboard/ConfirmEmailBanner"
import { WhyQuestions } from "../shared/WhyQuestions"
import { CRITERIO_DE_PRONTO, CTA_DIAGNOSTICO, DIAGNOSTICO_SUBTITULO, PROMESSA } from "../../lib/narrativa"
import type { ProvaListItem, ProvasResponse } from "../../types"

// Divisas da marca (chevron triplo) — o mesmo motivo do logo, aqui decorativo.
function Chevrons({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="square" aria-hidden="true">
      <path d="M4 8.5 12 3.5 20 8.5" />
      <path d="M4 14 12 9 20 14" />
      <path d="M4 19.5 12 14.5 20 19.5" />
    </svg>
  )
}

/** Atalho pra atribuir o delay do reveal escalonado como CSS var. */
function delay(ms: number): React.CSSProperties {
  return { "--reveal-delay": `${ms}ms` } as React.CSSProperties
}

const STEPS: [string, string, string][] = [
  ["Faça seu diagnóstico", "10 questões oficiais do nivelamento, sem estudar antes. No fim: suas matérias de partida e o gabarito comentado de cada uma.", "🎯"],
  ["Estude pelo gabarito", "Não precisa estudar tudo. Cada questão traz a resposta certa e o comentário — estude só o que te derrubou.", "📖"],
  ["Treine até ficar pronto", `Curta pro dia a dia, Oficial no formato da banca. ${CRITERIO_DE_PRONTO}`, "🏁"],
]

// Distribuição da prova objetiva da ESA (últimos editais). Educa o lead sobre o concurso.
const SUBJECTS: [string, string | null][] = [
  ["Matemática", "14"],
  ["Português", "14"],
  ["História do Brasil", "6"],
  ["Geografia do Brasil", "6"],
  ["Inglês", "10"],
  ["Redação", null],
]

const REASSURANCE = [
  "Diagnóstico gratuito — sem cartão",
  "Depois dele, 1 diagnóstico grátis por matéria",
  "Plano só se quiser continuar: nada renova sozinho",
]

export default function Welcome() {
  const { user } = useUser()
  const { me } = useMe()
  const [provas, setProvas] = useState<ProvaListItem[] | null>(null)

  useEffect(() => {
    api
      .get<ProvasResponse>("/api/provas")
      .then((data) => setProvas(data.provas))
      .catch(() => setProvas([]))
  }, [])

  const nome = firstName(user?.name ?? null, "recruta")
  // O nivelamento só libera após confirmar o e-mail — é o passo de ativação.
  const needsConfirm = me != null && !me.confirmed
  // Funil de ativação: com uma prova só, manda direto pro ponto de partida dela;
  // com mais de uma, cai no painel pra escolher. Enquanto carrega, painel é o default seguro.
  const startTo = provas && provas.length === 1 ? `/prova/${provas[0].id}` : "/dashboard"
  const prova = provas?.[0] ?? null
  const days = prova?.days_until_exam ?? null

  return (
    <AppShell>
      <div className="flex flex-col gap-8">
        {/* Passo de ativação: confirmar o e-mail libera o nivelamento. */}
        {needsConfirm && (
          <div className="reveal-up" style={delay(0)}>
            <ConfirmEmailBanner />
          </div>
        )}

        {/* ============ HERO ============ */}
        <section
          className="reveal-up relative overflow-hidden rounded-3xl bg-navy-950 px-6 py-10 shadow-xl shadow-navy-900/25 sm:px-10 sm:py-14"
          style={delay(0)}
        >
          {/* atmosfera: brilho dourado + textura diagonal (mesma linguagem da landing) */}
          <div
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_85%_60%_at_50%_-5%,rgba(212,166,42,0.16),transparent_65%)]"
            aria-hidden="true"
          />
          <div
            className="pointer-events-none absolute inset-0 bg-[repeating-linear-gradient(-45deg,rgba(212,166,42,0.04)_0px,rgba(212,166,42,0.04)_1px,transparent_1px,transparent_14px)]"
            aria-hidden="true"
          />
          <Chevrons className="pointer-events-none absolute -right-10 -top-10 h-52 w-52 text-white/[0.04]" />

          <div className="relative">
            {/* A carta do estreante: manchete que corrige a crença que trava o
                funil ("primeiro estudo tudo, depois faço questões"), uma frase
                por parágrafo, negrito só em frases inteiras. O número (75% vs
                56%) fica pro resultado e pra landing. */}
            <p className="font-display text-xs font-bold uppercase tracking-[0.28em] text-gold-400 sm:text-sm">
              Sua preparação
            </p>
            <p className="mt-4 text-lg text-navy-100">Olá, {nome}.</p>
            <h1 className="mt-2 max-w-2xl font-display text-4xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-6xl">
              Não estude antes do <span className="text-gold-400">nivelamento</span>.
            </h1>
            <div className="mt-5 max-w-xl space-y-3 text-base leading-relaxed text-navy-200 sm:text-lg">
              <p>Quem começa pelas questões aprende mais do que quem começa pela apostila.</p>
              <p className="font-semibold text-white">Errar no primeiro é o esperado.</p>
              <p>O nivelamento é grátis, leva 15 minutos e mostra exatamente suas matérias de partida.</p>
              <p>
                Aí, sim, estude só isso e <span className="font-semibold text-white">{PROMESSA.charAt(0).toLowerCase() + PROMESSA.slice(1)}</span>
              </p>
              {needsConfirm && (
                <p className="text-gold-200">
                  Só falta um passo: confirme seu e-mail (te enviamos um link) pra liberar seu diagnóstico.
                </p>
              )}
            </div>

            <div className="mt-8">
              <Link
                to={startTo}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gold-400 px-8 py-4 text-center font-display text-lg font-bold uppercase tracking-wider text-navy-950 shadow-[0_0_40px_rgba(212,166,42,0.35)] transition hover:bg-gold-300 sm:w-auto"
              >
                {CTA_DIAGNOSTICO}
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path fillRule="evenodd" d="M7.21 4.23a.75.75 0 011.06.02l5 5.25a.75.75 0 010 1.04l-5 5.25a.75.75 0 11-1.08-1.04L11.69 10 7.23 5.29a.75.75 0 01-.02-1.06z" clipRule="evenodd" />
                </svg>
              </Link>
            </div>

            {days != null && (
              <p className="mt-7 flex items-center gap-2 text-xs text-navy-300 sm:text-sm">
                <Chevrons className="h-4 w-4 shrink-0 text-gold-500" />
                Faltam <span className="font-display font-bold text-gold-400 tabular-nums">{days}</span> dias para a prova
                da ESA — a data está marcada, o treino começa agora.
              </p>
            )}
          </div>
        </section>

        {/* ============ COMO FUNCIONA ============ */}
        <section className="reveal-up" style={delay(120)} aria-label="Como funciona">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-navy-500">Como funciona em 3 passos</h2>
          <div className="grid gap-4 sm:grid-cols-3">
            {STEPS.map(([title, text, emoji], i) => (
              <div key={title} className="relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex items-center justify-between">
                  <span className="font-display text-3xl font-bold text-gold-400/90">{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-2xl" aria-hidden="true">{emoji}</span>
                </div>
                <h3 className="mt-3 font-display text-lg font-semibold uppercase tracking-wide text-navy-900">{title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-navy-600">{text}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ============ O QUE CAI NA PROVA ============ */}
        <section className="reveal-up" style={delay(200)} aria-label="O que cai na prova da ESA">
          <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-navy-500">O que cai na prova da ESA</h2>
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <p className="text-sm text-navy-600">
              A prova objetiva tem <span className="font-semibold text-navy-900">50 questões em 4 horas</span>. Nossos
              simulados seguem a distribuição da banca:
            </p>
            <ul className="mt-4 flex flex-wrap gap-2">
              {SUBJECTS.map(([subject, count]) => (
                <li
                  key={subject}
                  className="flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3.5 py-1.5 text-sm text-navy-700"
                >
                  {subject}
                  {count && <span className="font-display font-bold text-gold-600">{count}</span>}
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs leading-relaxed text-navy-400">
              A parte objetiva é o que elimina a maioria dos candidatos — é nela que a gente te prepara. A redação é
              discursiva e feita na prova oficial.
            </p>
          </div>
        </section>

        {/* ============ POR QUE QUESTÕES ============ */}
        <div className="reveal-up" style={delay(240)}>
          <WhyQuestions />
        </div>

        {/* ============ DIAGNÓSTICO GRATUITO → PLANO ============ */}
        <section className="reveal-up" style={delay(280)} aria-label="O diagnóstico gratuito e como funciona o plano">
          <div className="rounded-2xl border border-slate-200 bg-navy-50/60 p-5 shadow-sm sm:p-6">
            <h2 className="font-display text-xl font-bold uppercase tracking-wide text-navy-900 sm:text-2xl">
              Seu diagnóstico é gratuito — o plano fecha a distância
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-navy-600">
              {DIAGNOSTICO_SUBTITULO} Depois dele, um diagnóstico por matéria — nada disso custa nada. Fechar a
              distância até o corte é o plano: pago uma vez por período, sem nada renovando sozinho no seu cartão.
            </p>
            <ul className="mt-4 grid gap-2.5 sm:grid-cols-3">
              {REASSURANCE.map((item) => (
                <li key={item} className="flex items-start gap-2 text-sm text-navy-700">
                  <svg viewBox="0 0 20 20" fill="currentColor" className="mt-0.5 h-5 w-5 shrink-0 text-gold-500" aria-hidden="true">
                    <path fillRule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm3.86-9.35a.75.75 0 0 0-1.22-.88l-2.96 4.1-1.4-1.4a.75.75 0 1 0-1.06 1.06l2 2a.75.75 0 0 0 1.14-.09l3.5-4.79Z" clipRule="evenodd" />
                  </svg>
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ============ CTA FINAL ============ */}
        <section
          className="reveal-up flex flex-col items-center gap-4 rounded-2xl border border-gold-400 bg-gold-50/70 px-6 py-8 text-center shadow-sm"
          style={delay(360)}
        >
          <Chevrons className="h-8 w-8 text-gold-500" />
          <h2 className="font-display text-2xl font-bold uppercase leading-tight tracking-tight text-navy-900 sm:text-3xl">
            Pronto pra começar?
          </h2>
          <p className="max-w-md text-sm text-navy-600">
            Seu diagnóstico não custa nada. É só descobrir onde você perde ponto e por onde começar.
          </p>
          <Link
            to={startTo}
            className="inline-flex items-center justify-center gap-2 rounded-xl bg-gold-400 px-8 py-4 font-display text-lg font-bold uppercase tracking-wider text-navy-950 shadow-sm transition hover:bg-gold-300"
          >
            {CTA_DIAGNOSTICO}
          </Link>
        </section>
      </div>
    </AppShell>
  )
}
